/**
 * PR-35D.1 — Live Theme Application.
 * Extends PR-35D's Team Theme Engine with runtime glow/soft variants,
 * cinematic micro-pulses for emotional moments, Pro Analytics dampening,
 * and a lightweight subscription model for components.
 * Does NOT replace PR-35D; it consumes and enriches it.
 */
import { trackEvent } from "@/lib/audit";
import {
  type TeamId,
  applyTeamPalette,
  getActiveThemeId,
  getCustomPalette,
  getPaletteById,
} from "@/lib/pr35d/teamThemeEngine";
import { applyIdentityTokens } from "@/lib/pr36/themeIdentityTokens";

export type ThemeZone =
  | "header"
  | "cta"
  | "radar"
  | "score"
  | "timeline"
  | "charts"
  | "progress"
  | "tabs"
  | "rewards"
  | "notification"
  | "secondary_button";

export const THEME_ZONES: ThemeZone[] = [
  "header", "cta", "radar", "score", "timeline",
  "charts", "progress", "tabs", "rewards", "notification", "secondary_button",
];

/**
 * Apply the active palette and derive soft/glow/gradient variants so that
 * `.text-team`, `.bg-team-soft`, `.glow-team`, `.gradient-team` reflect the
 * user selection immediately.
 */
export function applyLiveTheme(id?: TeamId): void {
  const activeId = id ?? getActiveThemeId();
  const custom = activeId === "CUSTOM" ? getCustomPalette() : null;
  const applied = applyTeamPalette(activeId, custom);
  if (typeof document !== "undefined") {
    const r = document.documentElement.style;
    r.setProperty("--team-glow",
      `0 0 24px color-mix(in oklab, ${applied.primary} 45%, transparent)`);
    r.setProperty("--team-glow-strong",
      `0 0 36px color-mix(in oklab, ${applied.primary} 60%, transparent)`);
    r.setProperty("--team-soft",
      `color-mix(in oklab, ${applied.primary} 18%, transparent)`);
    r.setProperty("--team-ambient",
      `radial-gradient(60% 80% at 50% 0%, color-mix(in oklab, ${applied.primary} 22%, transparent), transparent 70%)`);
    r.setProperty("--team-ring",
      `0 0 0 1px color-mix(in oklab, ${applied.primary} 55%, transparent)`);
    r.setProperty("--team-gradient", applied.gradient);
    // PR-36 — Emotional identity tokens (glow, aura, heat, tactical
    // highlight, overlay accent, pulse, contrast, stadium aura).
    applyIdentityTokens(applied.id, applied);
    // Expose active team as an attribute so CSS can conditionally enable
    // accents only when the user has chosen a real team (not DEFAULT).
    document.documentElement.setAttribute("data-team", applied.id);
    // Broadcast a runtime event so live components can react without
    // polling, in addition to the existing onLiveThemeChange listeners.
    try {
      window.dispatchEvent(new CustomEvent("vs:theme-change", { detail: { team: applied.id } }));
    } catch { /* noop */ }
    notifyLiveThemeChange();
  }
  void trackEvent("live_theme_applied", {
    team: applied.id,
    zones: THEME_ZONES.length,
  });
}

/** Cinematic micro-pulse for goals, danger plays or community euphoria. */
export function triggerTeamPulse(
  trigger: "goal" | "danger" | "euphoria" | "score_change",
  ms = 1200,
): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.classList.add("pulse-team");
  window.setTimeout(() => root.classList.remove("pulse-team"), Math.min(ms, 2000));
  void trackEvent("team_pulse_triggered", { trigger, ms });
}

/** Dampen the active palette while Pro Analytics Mode is on. */
export function applyProModeBalance(enabled: boolean): void {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("pro-mode", enabled);
  void trackEvent("pro_mode_color_balanced", { enabled });
}

/** Cinematic preview used by Settings → no persistence side effects beyond engine. */
export function previewTheme(id: TeamId): void {
  applyLiveTheme(id);
  void trackEvent("theme_preview_changed", { team: id });
}

export function describeActiveTheme(): {
  id: TeamId; label: string; flag: string; primary: string;
} {
  const id = getActiveThemeId();
  const p = getPaletteById(id);
  return { id, label: p.label, flag: p.flag, primary: p.primary };
}

const LISTENERS = new Set<() => void>();
export function onLiveThemeChange(fn: () => void): () => void {
  LISTENERS.add(fn);
  return () => LISTENERS.delete(fn);
}
export function notifyLiveThemeChange(): void {
  LISTENERS.forEach((fn) => { try { fn(); } catch { /* noop */ } });
}